import React, { useState, useEffect, useCallback } from 'react';
import { Accordion, Button, Card, Col, Row, Spinner, Modal, Form, InputGroup, Table, Alert } from 'react-bootstrap';
import axios from 'axios';

const ConveniosTab = ({ currentEntidade, isActive, formatCurrency }) => {
    const [convenios, setConvenios] = useState([]);
    const [servicos, setServicos] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    // State for Modals
    const [showConvenioModal, setShowConvenioModal] = useState(false);
    const [showPlanoModal, setShowPlanoModal] = useState(false);
    const [showCoberturaModal, setShowCoberturaModal] = useState(false);

    // State for currently edited/created item
    const [currentConvenio, setCurrentConvenio] = useState(null);
    const [currentPlano, setCurrentPlano] = useState(null);
    const [currentCobertura, setCurrentCobertura] = useState(null);

    const fetchData = useCallback(async () => {
        if (!isActive || !currentEntidade?.id) return;
        setIsLoading(true);
        setError(null);
        try {
            // Presume-se que o backend agora retorna os convênios com seus planos e coberturas aninhados
            const [conveniosRes, servicosRes] = await Promise.all([
                axios.get(`/api/convenios?entidade_id=${currentEntidade.id}`),
                axios.get(`/api/servicos?entidade_id=${currentEntidade.id}`)
            ]);
            setConvenios(conveniosRes.data.convenios || []);
            setServicos(servicosRes.data.servicos || []);
        } catch (err) {
            setError('Falha ao carregar dados de convênios e serviços.');
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    }, [isActive, currentEntidade?.id]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // --- Handlers for Convenio ---
    const handleOpenConvenioModal = (convenio = null) => {
        setCurrentConvenio(convenio || { nome: '', cnpj: '', registro_ans: '', ativo: true, entidade_id: currentEntidade.id });
        setShowConvenioModal(true);
    };

    const handleSaveConvenio = async () => {
        const url = currentConvenio.id ? `/api/convenios/${currentConvenio.id}` : '/api/convenios';
        const method = currentConvenio.id ? 'put' : 'post';
        try {
            // Usa o método dinâmico (post/put) e envia os dados do convênio
            await axios({
                method,
                url,
                data: currentConvenio, 
            });
            setShowConvenioModal(false);
            fetchData(); // Refresh data
        } catch (err) {
            alert('Erro ao salvar convênio: ' + (err.response?.data?.message || err.message));
        }
    };

    // --- Handlers for Plano ---
    const handleOpenPlanoModal = (convenioId, plano = null) => {
        setCurrentPlano(plano || { nome: '', descricao: '', ativo: true, convenio_id: convenioId });
        setShowPlanoModal(true);
    };

    const handleSavePlano = async () => {
        const url = currentPlano.id ? `/api/convenio-planos/${currentPlano.id}` : '/api/convenio-planos';
        const method = currentPlano.id ? 'put' : 'post';
        try {
            await axios({
                method,
                url,
                data: currentPlano, 
            });
            setShowPlanoModal(false);
            fetchData(); // Refresh data
        } catch (err) {
            alert('Erro ao salvar plano: ' + (err.response?.data?.message || err.message));
        }
    };

    // --- Handlers for Cobertura ---
    const handleOpenCoberturaModal = (planoId, cobertura = null) => {
        setCurrentCobertura(cobertura || {
            servico_id: '',
            tipo_cobertura: 'percentual',
            valor_cobertura: 0,
            tipo_coparticipacao: 'percentual',
            valor_coparticipacao: 0,
            convenio_plano_id: planoId
        });
        setShowCoberturaModal(true);
    };

    const handleSaveCobertura = async () => {
        if (!currentCobertura.servico_id) {
            alert('Por favor, selecione um serviço.');
            return;
        }
        const url = currentCobertura.id ? `/api/convenio-coberturas/${currentCobertura.id}` : '/api/convenio-coberturas';
        const method = currentCobertura.id ? 'put' : 'post';
        try {
            await axios({
                method,
                url,
                data: currentCobertura, // aqui vão os dados que você quer enviar
            });
            setShowCoberturaModal(false);
            fetchData(); // Refresh data
        } catch (err) {
            alert('Erro ao salvar cobertura: ' + (err.response?.data?.message || err.message));
        }
    };

    if (isLoading) {
        return <div className="text-center mt-5"><Spinner animation="border" /> Carregando...</div>;
    }

    if (error) {
        return <Alert variant="danger">{error}</Alert>;
    }

    return (
        <>
            <Card>
                <Card.Header>
                    <Row className="align-items-center">
                        <Col>
                            <h5 className="mb-0">Gestão de Convênios, Planos e Coberturas</h5>
                        </Col>
                        <Col xs="auto">
                            <Button variant="success" onClick={() => handleOpenConvenioModal()}>
                                Novo Convênio
                            </Button>
                        </Col>
                    </Row>
                </Card.Header>
                <Card.Body>
                    {convenios.length === 0 ? (
                        <p>Nenhum convênio cadastrado.</p>
                    ) : (
                        <Accordion defaultActiveKey="0">
                            {convenios.map(convenio => (
                                <Accordion.Item eventKey={convenio.id.toString()} key={convenio.id}>
                                    <Accordion.Header>{convenio.nome}</Accordion.Header>
                                    <Accordion.Body>
                                        <div className="d-flex justify-content-end mb-3">
                                            <Button variant="outline-primary" size="sm" onClick={() => handleOpenConvenioModal(convenio)}>Editar Convênio</Button>
                                            <Button variant="primary" size="sm" className="ms-2" onClick={() => handleOpenPlanoModal(convenio.id)}>Adicionar Plano</Button>
                                        </div>
                                        <h6>Planos de Saúde</h6>
                                        {convenio.planos && convenio.planos.length > 0 ? (
                                            <Accordion>
                                                {convenio.planos.map(plano => (
                                                    <Accordion.Item eventKey={plano.id.toString()} key={plano.id}>
                                                        <Accordion.Header>{plano.nome}</Accordion.Header>
                                                        <Accordion.Body>
                                                            <div className="d-flex justify-content-end mb-3">
                                                                <Button variant="outline-secondary" size="sm" onClick={() => handleOpenPlanoModal(convenio.id, plano)}>Editar Plano</Button>
                                                                <Button variant="info" size="sm" className="ms-2" onClick={() => handleOpenCoberturaModal(plano.id)}>Adicionar Cobertura</Button>
                                                            </div>
                                                            <h6>Regras de Cobertura</h6>
                                                            {plano.coberturas && plano.coberturas.length > 0 ? (
                                                                <Table striped bordered hover responsive size="sm">
                                                                    <thead>
                                                                        <tr>
                                                                            <th>Serviço</th>
                                                                            <th>Coparticipação (Paciente)</th>
                                                                            <th>Ações</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {plano.coberturas.map(cobertura => (
                                                                            <tr key={cobertura.id}>
                                                                                <td>{cobertura.servico?.nome || 'Serviço não encontrado'}</td>
                                                                                <td>
                                                                                    {cobertura.tipo_coparticipacao === 'percentual'
                                                                                        ? `${cobertura.valor_coparticipacao}%`
                                                                                        : formatCurrency(cobertura.valor_coparticipacao)}
                                                                                </td>
                                                                                <td>
                                                                                    <Button variant="outline-info" size="sm" onClick={() => handleOpenCoberturaModal(plano.id, cobertura)}>
                                                                                        Editar
                                                                                    </Button>
                                                                                </td>
                                                                            </tr>
                                                                        ))}
                                                                    </tbody>
                                                                </Table>
                                                            ) : <p>Nenhuma regra de cobertura cadastrada para este plano.</p>}
                                                        </Accordion.Body>
                                                    </Accordion.Item>
                                                ))}
                                            </Accordion>
                                        ) : <p>Nenhum plano cadastrado para este convênio.</p>}
                                    </Accordion.Body>
                                </Accordion.Item>
                            ))}
                        </Accordion>
                    )}
                </Card.Body>
            </Card>

            {/* Modal Convênio */}
            <Modal show={showConvenioModal} onHide={() => setShowConvenioModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>{currentConvenio?.id ? 'Editar' : 'Novo'} Convênio</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label>Nome da Operadora *</Form.Label>
                            <Form.Control type="text" value={currentConvenio?.nome || ''} onChange={e => setCurrentConvenio({ ...currentConvenio, nome: e.target.value })} required />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>CNPJ</Form.Label>
                            <Form.Control type="text" value={currentConvenio?.cnpj || ''} onChange={e => setCurrentConvenio({ ...currentConvenio, cnpj: e.target.value })} />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Registro ANS</Form.Label>
                            <Form.Control type="text" value={currentConvenio?.registro_ans || ''} onChange={e => setCurrentConvenio({ ...currentConvenio, registro_ans: e.target.value })} />
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowConvenioModal(false)}>Cancelar</Button>
                    <Button variant="primary" onClick={handleSaveConvenio}>Salvar</Button>
                </Modal.Footer>
            </Modal>

            {/* Modal Plano */}
            <Modal show={showPlanoModal} onHide={() => setShowPlanoModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>{currentPlano?.id ? 'Editar' : 'Novo'} Plano</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label>Nome do Plano *</Form.Label>
                            <Form.Control type="text" value={currentPlano?.nome || ''} onChange={e => setCurrentPlano({ ...currentPlano, nome: e.target.value })} required />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Descrição</Form.Label>
                            <Form.Control as="textarea" rows={2} value={currentPlano?.descricao || ''} onChange={e => setCurrentPlano({ ...currentPlano, descricao: e.target.value })} />
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowPlanoModal(false)}>Cancelar</Button>
                    <Button variant="primary" onClick={handleSavePlano}>Salvar</Button>
                </Modal.Footer>
            </Modal>

            {/* Modal Cobertura */}
            <Modal show={showCoberturaModal} onHide={() => setShowCoberturaModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>{currentCobertura?.id ? 'Editar' : 'Nova'} Regra de Cobertura</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label>Serviço *</Form.Label>
                            <Form.Select value={currentCobertura?.servico_id || ''} onChange={e => setCurrentCobertura({ ...currentCobertura, servico_id: e.target.value })} required disabled={!!currentCobertura?.id}>
                                <option value="">Selecione um serviço...</option>
                                {servicos.map(servico => (
                                    <option key={servico.id} value={servico.id}>{servico.nome}</option>
                                ))}
                            </Form.Select>
                            {currentCobertura?.id && <Form.Text muted>O serviço não pode ser alterado após a criação da regra.</Form.Text>}
                        </Form.Group>
                        <hr/>
                        <p className="fw-bold">Regra de Coparticipação (Valor pago pelo Paciente)</p>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Tipo de Coparticipação</Form.Label>
                                    <Form.Select value={currentCobertura?.tipo_coparticipacao || 'percentual'} onChange={e => setCurrentCobertura({ ...currentCobertura, tipo_coparticipacao: e.target.value })}>
                                        <option value="percentual">Percentual (%)</option>
                                        <option value="valor_fixo">Valor Fixo (R$)</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Valor da Coparticipação</Form.Label>
                                    <InputGroup>
                                        {currentCobertura?.tipo_coparticipacao === 'valor_fixo' && <InputGroup.Text>R$</InputGroup.Text>}
                                        <Form.Control type="number" step="0.01" value={currentCobertura?.valor_coparticipacao || 0} onChange={e => setCurrentCobertura({ ...currentCobertura, valor_coparticipacao: e.target.value })} />
                                        {currentCobertura?.tipo_coparticipacao === 'percentual' && <InputGroup.Text>%</InputGroup.Text>}
                                    </InputGroup>
                                </Form.Group>
                            </Col>
                        </Row>
                        <Alert variant="info">
                            Exemplo: Se a coparticipação for de 20%, o paciente pagará 20% do valor do serviço e o convênio cobrirá os 80% restantes.
                        </Alert>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowCoberturaModal(false)}>Cancelar</Button>
                    <Button variant="primary" onClick={handleSaveCobertura}>Salvar Regra</Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default ConveniosTab;
